import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { apiInstance } from "@/shared/api";
import type {
  CreateLeadLinkRequest,
  DeleteLinkRequest,
  GetLeadLinkInfoRequest,
  GetLeadLinkInfoResponse,
  GetLinksRequest,
  UpdateLinkRequest,
} from "./api.dto";
import { useMutate } from "@/shared/hooks";
import { filterObjectKeys } from "@/shared/lib/utils";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import type { PaginatedResponse } from "@/shared/types";
import type { LeadLinkShares, LeadLink } from "../model";
import { V2PaginatedResponse } from "@/shared/types/api.v2";

export const useCreateLink = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/lead-links/create",
    toast: {
      success: {
        description: "Ссылка создана.",
      },
    },
    refetchQueries: [["leadLinks"], ["brands"]],
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    CreateLeadLinkRequest,
    Response
  >({
    mutationKey: ["createLink"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useLeadLinkInfo = (
  params: GetLeadLinkInfoRequest & { enabled?: boolean },
) => {
  return useQuery<GetLeadLinkInfoResponse>({
    queryKey: ["leadsLinks", "info"],
    queryFn: async () => {
      const { data } = await apiInstance.get<GetLeadLinkInfoResponse>(
        `/lead-links/${params.leadId}/info`,
        undefined,
        true,
      );

      return data;
    },
    enabled: params.enabled ?? true,
  });
};

export const useLeadLinks = (params: GetLinksRequest) => {
  const queryParams = filterObjectKeys(
    {
      ...params,
      lastCreatedAt:
        "lastId" in params && params.lastId ? params.lastCreatedAt : null,
    },
    ["userId"],
  );

  return useQuery<PaginatedResponse<LeadLink>>({
    queryKey: ["leadLinks", params.userId, queryParams],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<LeadLink>>(
        `/lead-links/user/${params.userId}`,
        queryParams,
      );

      return data;
    },
  });
};

export const useLeadLinksInf = (
  params: {
    userId: string;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: string;
    type?: string;
  },
  { enabled }: { enabled: boolean },
) => {
  const queryParams = filterObjectKeys(params, ["userId"]);

  return useInfiniteQuery({
    queryKey: ["leadLinks", params.userId, queryParams],
    initialPageParam: {
      lastCreatedAt: null as string | null,
      lastId: null as string | null,
    },
    queryFn: async ({ pageParam }) => {
      const cursorParams = filterObjectKeys({
        ...pageParam,
        lastCreatedAt: pageParam.lastId ? pageParam.lastCreatedAt : null,
      });
      const { data } = await apiInstance.get<V2PaginatedResponse<LeadLink>>(
        `/lead-links/user/${params.userId}`,
        {
          ...queryParams,
          ...cursorParams,
        },
      );

      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce(
        (accumulator, page) => accumulator + page.data.length,
        0,
      );

      if (loadedItems >= lastPage.total) {
        return undefined;
      }

      const lastLink = lastPage.data[lastPage.data.length - 1];

      if (!lastLink) {
        return undefined;
      }

      return {
        lastCreatedAt:
          lastLink.createdAt instanceof Date
            ? lastLink.createdAt.toISOString()
            : new Date(lastLink.createdAt).toISOString(),
        lastId: lastLink._id,
      };
    },
    enabled,
  });
};

export const useArchiveLink = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Ссылка архивирована.",
      },
    },
    refetchQueries: ["leadLinks"],
  });
  return useMutation<AxiosResponse, AxiosError, DeleteLinkRequest, Response>({
    mutationKey: ["deleteLink"],
    mutationFn: (data) => {
      return apiInstance.post(`/lead-links/${data.id}/archive`);
    },
    onSuccess,
    onError,
  });
};

export const useRestoreLink = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Ссылка архивирована.",
      },
    },
    refetchQueries: ["leadLinks"],
  });
  return useMutation<AxiosResponse, AxiosError, DeleteLinkRequest, Response>({
    mutationKey: ["restoreLink"],
    mutationFn: (data) => {
      return apiInstance.post(`/lead-links/${data.id}/unarchive`);
    },
    onSuccess,
    onError,
  });
};

export const useLeadLinksShares = ({
  linkId,
  enabled,
}: {
  linkId?: string;
  enabled: boolean;
}) => {
  return useQuery<LeadLinkShares>({
    queryKey: ["leadLinksShares", linkId],
    queryFn: async () => {
      try {
        const { data } = await apiInstance.get<LeadLinkShares>(
          `/lead-links/${linkId}/shares`,
        );

        return data;
      } catch (error) {
        console.error(error);
        throw new Error("Failed to fetch lead links shares");
      }
    },
    enabled: enabled && !!linkId,
  });
};

export const useUpdateLink = (linkId: string) => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "patch",
    url: `/lead-links/${linkId}`,
    toast: {
      success: { title: "Ссылка успешно обновлена" },
      error: { title: "Ошибка при обновлении ссылки" },
    },
    refetchQueries: ["leadLinks"],
  });

  return useMutation<AxiosResponse, AxiosError, UpdateLinkRequest>({
    mutationKey: ["updateLink", linkId],
    mutationFn,
    onSuccess,
    onError,
  });
};
